import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { toast } from 'react-toastify';

import {
  setCommonAction,
  setSimpleNotasFiscaisAction,
} from './NotasFiscais.store';

import {
  SET_BUSCAR_DADOS_TABELA,
  SET_EXPORTAR_NOTAS_FISCAIS,
  SET_CANCELAR_NOTA_FISCAL,
} from './NotasFiscais.constants';
import {
  cancelarNotaFiscal, downloadNotasFiscaisExcel, downloadProtocoloCancelamentoNF, pesquisarNotasFiscaisPage, pesquisarResumoNotasFiscaisPage,
} from '../../services/core/contaCorrenteClienteService/ContaCorrenteClienteService';
import { downloadFileSispag } from '../../helpers/utils';
import {
  buildNotasFiscaisPageable,
  buildNotasFiscaisPayload,
} from '../../pages/NotasFiscais/TableNotasFiscais/NotasFiscaisFactory';
import { DOWNLOAD_PROTOCOLO_CANCELAMENTO_NF_ACTION, VISUALIZAR_NOTA_FISCAL_CLIENTE_ACTION } from '../ContaCorrenteCliente/contaCorrenteCliente.contants';
import { openPageFile, toastUnmappedException } from '../../helpers';


function* successPesquisarNotasFiscais(data) {
  const content = Array.isArray(data)
    ? data
    : (data?.content || data?.items || data?.dados || []);
  const total = data?.totalElements ?? content.length;

  yield put(setSimpleNotasFiscaisAction('tableView', content));
  yield put(setSimpleNotasFiscaisAction('totalElements', total));
  yield put(setSimpleNotasFiscaisAction('rowsPerPage', data?.pageable?.pageSize || 10));
  yield put(setSimpleNotasFiscaisAction('page', data?.pageable?.pageNumber || 0));
}

function* successResumoNotasFiscais(data) {
  yield put(setSimpleNotasFiscaisAction('resumoNotasFiscais', {
    ativaCliente: data?.ativaCliente ?? 0,
    canceladaCliente: data?.canceladaCliente ?? 0,
    foraPrazoCliente: data?.foraPrazoCliente ?? 0,
    ativaDevedor: data?.ativaDevedor ?? 0,
    canceladaDevedor: data?.canceladaDevedor ?? 0,
    foraPrazoDevedor: data?.foraPrazoDevedor ?? 0,
  }));
}

function* emptyPesquisaNotasFiscais() {
  yield put(setSimpleNotasFiscaisAction('tableView', []));
  yield put(setSimpleNotasFiscaisAction('totalElements', 0));
  yield put(setSimpleNotasFiscaisAction('resumoNotasFiscais', {
    ativaCliente: 0,
    canceladaCliente: 0,
    foraPrazoCliente: 0,
    ativaDevedor: 0,
    canceladaDevedor: 0,
    foraPrazoDevedor: 0,
  }));
}

function* getNotasFiscaisSearchContext() {
  const filtrosRequest = yield select(states => states.filtrosRequest || {});
  const periodoInicial = yield select(states => states.periodoInicial?.value || '');
  const periodoFinal = yield select(states => states.periodoFinal?.value || '');
  const page = yield select(states => states.page ?? 0);
  const rowsPerPage = yield select(states => states.rowsPerPage ?? 10);
  const columnOrdenacao = yield select(
    states => states.columnOrdenacao || { element: 'dataNota', order: 'DESC' },
  );

  const payload = buildNotasFiscaisPayload({
    filtrosRequest,
    periodoInicial,
    periodoFinal,
  });

  const pageable = buildNotasFiscaisPageable({
    page,
    rowsPerPage,
    columnOrdenacao,
  });

  return {
    payload,
    pageable,
    columnOrdenacao,
  };
}

function* buscarDadosTabelaHandler() {
  yield put(setCommonAction('loadingPage', true));

  try {
    const { payload, pageable } = yield getNotasFiscaisSearchContext();

    const [responseNotas, responseResumo] = yield all([
      call(pesquisarNotasFiscaisPage, payload, pageable),
      call(pesquisarResumoNotasFiscaisPage, payload),
    ]);

    if (responseNotas?.data.content.length === 0) {
      toast.warn('Nenhum resultado foi encontrado relacionado à sua pesquisa.');
    }
    yield successPesquisarNotasFiscais(responseNotas?.data);
    yield successResumoNotasFiscais(responseResumo?.data);
  } catch (e) {
    yield emptyPesquisaNotasFiscais();
    toast.error('Erro ao buscar notas fiscais. Tente novamente mais tarde.');
  } finally {
    yield put(setCommonAction('loadingPage', false));
  }
}

function* exportarNotasFiscaisHandler() {
  yield put(setCommonAction('loadingPage', true));

  try {
    const { payload } = yield getNotasFiscaisSearchContext();

    const response = yield call(downloadNotasFiscaisExcel, payload);
    if (response?.data) {
      downloadFileSispag(response?.headers?.filename, response?.data);
      toast.success('Arquivo exportado com sucesso!');
    } else {
      toast.info('Nenhuma nota fiscal encontrada para exportação.');
    }
  } catch (e) {
    toast.error('Erro ao gerar o Excel. Tente novamente mais tarde.');
  } finally {
    yield put(setCommonAction('loadingPage', false));
  }
}

function* cancelarNotaFiscalHandler(actions) {
  yield put(setCommonAction('loadingPage', true));
  const { idNotaFiscal } = actions.payload || {};

  try {
    yield call(cancelarNotaFiscal, idNotaFiscal);

    const { payload, pageable } = yield getNotasFiscaisSearchContext();

    const [responseNotas, responseResumo] = yield all([
      call(pesquisarNotasFiscaisPage, payload, pageable),
      call(pesquisarResumoNotasFiscaisPage, payload),
    ]);

    yield successPesquisarNotasFiscais(responseNotas?.data);
    yield successResumoNotasFiscais(responseResumo?.data);
    toast.success('Nota fiscal cancelada com sucesso.');
  } catch (e) {
    toast.error('Erro ao cancelar a nota fiscal.');
  } finally {
    yield put(setCommonAction('loadingPage', false));
  }
}

function* visualizarNotaFiscalClienteHandler(actions) {
  const { urlPrefeituraNF } = actions;
  yield put(setCommonAction('loadingPage', true));
  try {
    window.open(urlPrefeituraNF, '_blank');
  } catch (e) {
    toastUnmappedException('Erro na visualização de Nota Fiscal');
  } finally {
    yield put(setCommonAction('loadingPage', false));
  }
}

function* downloadProtocoloCancelamentoNFHandler(actions) {
  yield put(setCommonAction('loadingPage', true));
  const { idNotaFiscalCliente } = actions;
  try {
    const response = yield call(downloadProtocoloCancelamentoNF, idNotaFiscalCliente);
    if (response.data.size > 0) {
      openPageFile(response.data, response.data.type);
    }
  } catch (e) {
    toastUnmappedException(
      e,
      'Ocorreu um problema ao tentar abrir o relatório',
    );
  } finally {
    yield put(setCommonAction('loadingPage', false));
  }
}

export const buscarDadosTabelaAction = () => ({
  type: SET_BUSCAR_DADOS_TABELA,
});

export const exportarNotasFiscaisAction = () => ({
  type: SET_EXPORTAR_NOTAS_FISCAIS,
});

export const cancelarNotaFiscalAction = idNotaFiscal => ({
  type: SET_CANCELAR_NOTA_FISCAL,
  payload: {
    idNotaFiscal,
  },
});

export const visualizarNotaFiscalClienteAction = urlPrefeituraNF => ({
  type: VISUALIZAR_NOTA_FISCAL_CLIENTE_ACTION,
  urlPrefeituraNF,
});

export const downloadProtocoloCancelamentoNFAction = idNotaFiscalCliente => ({
  type: DOWNLOAD_PROTOCOLO_CANCELAMENTO_NF_ACTION,
  idNotaFiscalCliente,
});

export default function* watchNotasFiscais() {
  yield takeLatest(SET_BUSCAR_DADOS_TABELA, buscarDadosTabelaHandler);
  yield takeLatest(SET_EXPORTAR_NOTAS_FISCAIS, exportarNotasFiscaisHandler);
  yield takeLatest(SET_CANCELAR_NOTA_FISCAL, cancelarNotaFiscalHandler);
  yield takeLatest(VISUALIZAR_NOTA_FISCAL_CLIENTE_ACTION, visualizarNotaFiscalClienteHandler);
  yield takeLatest(DOWNLOAD_PROTOCOLO_CANCELAMENTO_NF_ACTION, downloadProtocoloCancelamentoNFHandler);
}
